// Eskom PARCC — Users (admin).
//
// The user-management screen: a list of access grants on the left, an editor on
// the right, and the access activity feed below. Reads go through the Data
// Connect client (admin-claim enforced); writes go through the privileged
// admin-api (also admin-claim enforced). The route is gated by
// <RequireRole role="admin"> AND this page re-confirms the admin role before
// rendering the editor (defence-in-depth) — though the real boundary is the
// server-side claim check on every read and write.
import { useMemo, useState } from 'react';
import {
  DataTable,
  Button,
  Badge,
  complianceClass,
  LoadingState,
  ErrorState,
  EmptyState,
  Pager,
  usePagination,
  type Column,
} from '../../components/ui';
import { useAccessGrants } from '../../hooks';
import { useAuth } from '../../lib/auth';
import { ROLE_LABELS } from '../../lib/permissions';
import type { Role } from '../../lib/access';
import type { AccessGrantRecord } from '../../lib/db';
import { AccessGrantEditor } from './AccessGrantEditor';
import { AuditPanel } from './AuditPanel';

export function UsersPage(): React.JSX.Element {
  const { role } = useAuth();
  const grantsQuery = useAccessGrants();
  const [selectedEmail, setSelectedEmail] = useState<string | null>(null);
  const [editorMode, setEditorMode] = useState<'add' | 'edit' | null>(null);

  const isAdmin = role === 'admin';
  const grants = useMemo(() => grantsQuery.data ?? [], [grantsQuery.data]);
  // Page the roster — both themes (Lumen 7–8/page, Legacy 50).
  const pagination = usePagination(grants);
  const selected = useMemo(
    () => grants.find((g) => g.email === selectedEmail) ?? null,
    [grants, selectedEmail],
  );

  const columns = useMemo<Column<AccessGrantRecord>[]>(
    () => [
      {
        key: 'email',
        header: 'Email',
        sortable: true,
        accessor: (g) => g.email,
        render: (g) => g.email,
      },
      {
        key: 'displayName',
        header: 'Name',
        sortable: true,
        accessor: (g) => g.displayName ?? '',
        render: (g) => g.displayName ?? '—',
      },
      {
        key: 'role',
        header: 'Role',
        sortable: true,
        accessor: (g) => g.role,
        render: (g) => <Badge>{ROLE_LABELS[g.role as Role] ?? g.role}</Badge>,
      },
      {
        key: 'division',
        header: 'Division',
        sortable: true,
        accessor: (g) => g.division ?? '',
        render: (g) => {
          if (g.division) return g.division;
          // A reviewer / team lead with no home division sees only their own
          // allocations, never their division — the R4 "no access" symptom. Flag it.
          if (g.role === 'reviewer' || g.role === 'teamLead') {
            return (
              <Badge
                className={complianceClass('Non-Compliance: Possible IE')}
                title="No home division — this reviewer / team lead sees only their own allocations. Edit the grant to set one."
              >
                Needs division
              </Badge>
            );
          }
          return '—';
        },
      },
      {
        key: 'active',
        header: 'Status',
        sortable: true,
        accessor: (g) => (g.active ? 'Active' : 'Revoked'),
        render: (g) => <Badge>{g.active ? 'Active' : 'Revoked'}</Badge>,
      },
    ],
    [],
  );

  if (grantsQuery.isLoading) return <LoadingState message="Loading users…" />;
  if (grantsQuery.error) {
    const err = grantsQuery.error;
    return (
      <ErrorState
        message="Could not load the access list."
        detail={err instanceof Error ? err.message : String(err)}
        onRetry={() => void grantsQuery.refetch()}
      />
    );
  }

  return (
    <>
      <div className="split">
        <div className="card">
          <div className="section-head">
            <div>
              <h3>Users &amp; access</h3>
              <div className="small">{grants.length.toLocaleString('en-ZA')} user(s)</div>
            </div>
            <div className="actions">
              <Button
                variant="primary"
                disabled={!isAdmin}
                onClick={() => {
                  setSelectedEmail(null);
                  setEditorMode('add');
                }}
              >
                Add user
              </Button>
            </div>
          </div>
          {grants.length === 0 ? (
            <EmptyState message="No users yet. Use “Add user” to grant the first." />
          ) : (
            <DataTable
              columns={columns}
              rows={pagination.slice}
              getRowKey={(g) => g.email}
              onRowClick={(g) => {
                setSelectedEmail(g.email);
                setEditorMode('edit');
              }}
              initialSort={{ key: 'email', dir: 'asc' }}
              selectedRowKey={selectedEmail ?? undefined}
              emptyMessage="No users to display."
            />
          )}
          {grants.length > 0 && <Pager pagination={pagination} noun="user" />}
        </div>

        {!isAdmin ? (
          <EmptyState message="User management is restricted to administrators." />
        ) : editorMode === 'add' ? (
          <AccessGrantEditor mode="add" grant={null} onClose={() => setEditorMode(null)} />
        ) : editorMode === 'edit' && selected ? (
          <AccessGrantEditor
            key={selected.email}
            mode="edit"
            grant={selected}
            onClose={() => setEditorMode(null)}
          />
        ) : (
          <EmptyState message="Select a user to edit, or add a new one." />
        )}
      </div>

      <AuditPanel />
    </>
  );
}
